import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Scales, Barbell, Sparkle, Leaf, Shield, Heart, PaintBrush, Brain, Flask,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { RESEARCH_AREAS, bucketProducts } from "../../lib/research-areas";

// One icon per plain-English research area — kept in a lookup so a
// reorder in RESEARCH_AREAS never mis-pairs them.
const AREA_ICONS = {
  "weight-management":         Scales,
  "recovery-repair":           Sparkle,
  "growth-performance":        Barbell,
  "healthy-aging-longevity":   Leaf,
  "immune-support":            Shield,
  "hormone-sexual-wellness":   Heart,
  "skin-hair":                 PaintBrush,
  "focus-cognitive-research":  Brain,
  "other-research-compounds":  Flask,
};

/**
 * Plain-English "Shop by research area" grid.
 *
 * Renders the nine friendly buckets defined in `lib/research-areas.js`.
 * Every card deep-links into `/shop?area=<id>`, which ShopAll intercepts
 * and filters against the same rules used here — so the click path is
 * frictionless and every sellable SKU stays reachable via at least one
 * area (or the catch-all).
 */
export function HomeCategoryGrid() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    // 200-item fetch is enough — the catalog is 106 today.  Used purely
    // for showing the honest per-area COUNT badge; never invents.
    api.get("/products", { params: { limit: 200 } })
      .then((r) => setProducts(r.data.items || []))
      .catch(() => setProducts([]));
  }, []);

  const buckets = useMemo(() => bucketProducts(products), [products]);

  return (
    <section
      className="mps-container mps-section"
      data-testid="home-categories"
    >
      <div className="max-w-3xl mb-14">
        <div className="eyebrow mb-4" data-testid="home-categories-eyebrow">
          Shop by research area
        </div>
        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-brand-navy leading-tight">
          Find what you’re looking for
        </h2>
        <p className="mt-5 text-lg text-brand-muted leading-relaxed max-w-2xl">
          Browse by a familiar research topic, or view the full catalog.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7">
        {RESEARCH_AREAS.map((area) => {
          const Icon = AREA_ICONS[area.id] || Flask;
          const count = buckets.get(area.id)?.length || 0;
          return (
            <Link
              key={area.id}
              to={`/shop?area=${area.id}`}
              data-testid={area.testid}
              className="group flex flex-col gap-5 rounded-[22px] bg-white border border-brand-border p-7 lg:p-8 shadow-[0_2px_10px_rgba(10,35,66,0.05)] hover:shadow-[0_18px_44px_rgba(10,35,66,0.12)] hover:-translate-y-[3px] hover:border-brand-accent/40 transition-[transform,box-shadow,border-color] duration-200"
            >
              <div className="h-14 w-14 rounded-[16px] bg-gradient-to-br from-brand-accent/10 to-brand-accent/20 border border-brand-accent/20 flex items-center justify-center group-hover:from-brand-accent group-hover:to-brand-accent group-hover:border-brand-accent transition-colors duration-200">
                <Icon size={24} weight="regular" className="text-brand-accent group-hover:text-white transition-colors duration-200" aria-hidden />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-lg md:text-xl font-semibold text-brand-navy leading-tight tracking-tight">
                  {area.label}
                </h3>
                <p className="mt-2 text-sm text-brand-muted leading-relaxed">
                  {area.description}
                </p>
                {area.examples && (
                  <p className="mt-3 text-[12px] text-brand-muted">
                    e.g. {area.examples}
                  </p>
                )}
              </div>
              <div className="pt-4 flex items-center justify-between border-t border-brand-border">
                <span className="text-[12px] text-brand-muted">
                  {count > 0 ? `${count} peptide${count === 1 ? "" : "s"}` : "Browse group"}
                </span>
                <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-accent group-hover:text-brand-navy transition-colors">
                  Shop {area.label.split(/[\s&]/)[0]} <ArrowRight size={13} weight="bold" />
                </span>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Browse full catalog fallback — guarantees every sellable SKU is
          reachable even when a shopper doesn't match any topic bucket. */}
      <div className="mt-10 text-center">
        <Link
          to="/shop"
          data-testid="home-categories-browse-all"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-[12px] bg-brand-navy hover:bg-brand-navy2 text-white font-semibold text-sm transition-colors"
        >
          Browse the full catalog <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    </section>
  );
}
